"use client";

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useCheckoutBase, defaultGuest } from "./use-checkout-base";
import {
  resolveGuestCollectionMode,
  hasRenderableQuestions,
} from "../utils/guest-questions";
import { resolveSessionLocationName } from "../utils/session-location";
import { participantsToPayload } from "../participants-step";
import { buildTicketSeatGroups, totalSeatCount } from "../utils/ticket-seat-groups";
import { useV2Pricing } from "./use-v2-pricing";
import { effectivePreselectAvailability } from "./preselect-availability";
import { deriveV2SessionPrice, getLegacyManualPrice } from "../../../lib/pricing";
import type { CustomerBookingHistory } from "../../../lib/checkout/types";
import { codeBasePence } from "@obby/lib";
import type {
  Session,
  TicketType,
  TicketSelection,
  CheckoutStep,
  Guest,
} from "../utils/types";

export type UseCheckoutRegularProps = {
  course: any;
  session?: any;
  sessions?: any[];
  ticketTypes: any[];
  ticketTypesV2?: any[];
  /** IDs of ticket types the current buyer has already purchased for this
   *  course — drives locked/hidden display state for prior-purchase-gated
   *  V2 tickets. Sourced from the same course payload as `ticketTypesV2`. */
  priorPurchasedTicketIds?: string[];
  /** Clamped paid-booking counts for the current buyer at this school —
   *  drives the customer-history pricing conditions (FIRST_TIME_BOOKING,
   *  RETURNING_CUSTOMER, BOOKING_COUNT_IN_PERIOD) in the price preview.
   *  Sourced from the same course payload as `ticketTypesV2`. */
  customerBookingHistory?: CustomerBookingHistory;
  enabledAddOns?: any[];
  addOnGroups?: any[];
  locations?: any[]; // For location name resolution
  user?: any;
  bundleID?: string; // If using a purchased bundle, skip payment
  bundle?: any;
  isSendingMaterial?: boolean;
  /** Pre-select a session by ID (skips date selection step when session is found) */
  initialSessionId?: string;
  /** Seats reserved for this buyer per session (membership allocation) — added
   * to general availability when deciding sold-out, mirroring DateSelectionStep. */
  reservedSessionQuantities?: Record<string, number>;
  /**
   * Whether the listing allows the buyer to skip per-guest details.
   * Frozen and no longer consulted — buyer details are always collected
   * downstream in the basket checkout.
   */
  allowPrefillOthersGuestsOnCheckout?: boolean;
  /** Whether multi-date selection is enabled (requires basket) */
  isMultiDateEnabled?: boolean;
  /** Maximum number of dates a customer can select (multi-date mode) */
  maxDateSelections?: number;
  /** Whether the school collects attendees via the participants step. */
  participantsEnabled?: boolean;
  /** Whether the school has any checkout-eligible participant fields. */
  hasParticipantFields?: boolean;
  onLoadMoreDates: (args: {
    page: number;
    limit: number;
    sessionId?: string;
    fromDate?: string;
    locationId?: string;
  }) => Promise<any>;
  onValidateDiscount?: (code: string, email: string) => Promise<any>;
  onStepChange?: () => void;
};

/**
 * Hook for regular/drop-in course checkout
 * Flow: Dates -> Tickets -> AddOns -> UserForm -> Basket choice
 */
export function useCheckoutRegular({
  course,
  session,
  sessions: initialSessions,
  ticketTypes: initialTicketTypes,
  ticketTypesV2 = [],
  priorPurchasedTicketIds,
  customerBookingHistory,
  enabledAddOns = [],
  addOnGroups = [],
  locations = [],
  user,
  bundleID,
  bundle,
  isSendingMaterial = false,
  initialSessionId,
  reservedSessionQuantities,
  isMultiDateEnabled = false,
  maxDateSelections = 10,
  participantsEnabled = false,
  hasParticipantFields = false,
  onLoadMoreDates,
  onValidateDiscount,
  onStepChange,
}: UseCheckoutRegularProps) {
  const showParticipantsStep = participantsEnabled && hasParticipantFields;
  const isV2 = course?.ticketVersion === "v2";
  // Event listings own exactly one date, so there is nothing to pick: the date
  // step is dropped entirely and the sole session is selected as it loads.
  const isEventListing = (course as { listingType?: string })?.listingType === "event";
  const page = useRef(initialSessions === undefined ? 0 : 1);
  // Store raw sessions for ticket type filtering (sessions include ticketTypes array)
  const rawSessionsRef = useRef<any[]>(initialSessions ?? []);
  // Store ticket types ref for access in callbacks
  const ticketTypesRef = useRef<any[]>(initialTicketTypes ?? []);

  // Base checkout functionality
  const base = useCheckoutBase({
    user,
    enabledAddOns,
    addOnGroups,
    isSendingMaterial,
    onStepChange,
  });

  // Location NAME for a session (shared resolver — utils/session-location.ts).
  // undefined → the session row renders no location pin.
  const getSessionLocation = useCallback(
    (s: any): string | undefined => resolveSessionLocationName(s, locations),
    [locations],
  );

  // Helper to get lowest price from ticket types for a session
  // @MM-177: When course.isPriceFrom is true, use the course's configured price
  const getSessionPrice = useCallback(
    (s: any): number | undefined => {
      const legacyManualPrice = getLegacyManualPrice(course);
      if (legacyManualPrice !== undefined) {
        return legacyManualPrice;
      }

      const sessionTicketTypes = s.ticketTypes || [];
      const prices: number[] = [];
      const numberOfSessions =
        s?.numberOfSessions ?? (s?.additionalClasses?.length ?? 0) + 1;

      if (sessionTicketTypes.length > 0) {
        sessionTicketTypes.forEach((stt: any) => {
          if (stt.status !== "enabled") return;
          if (stt.maxLimit !== undefined && stt.maxLimit <= 0) return;

          const baseTicket = initialTicketTypes?.find(
            (tt: any) => tt._id === stt.ticketTypeId || tt._id === stt._id,
          );
          if (baseTicket?.price !== undefined) {
            prices.push(baseTicket.price);
            return;
          }

          // V1 ticketTypes has nothing for this id — try V2.
          const v2Ticket = ticketTypesV2.find((tt: any) => tt.id === stt.ticketTypeId);
          if (v2Ticket) {
            prices.push(deriveV2SessionPrice(v2Ticket, numberOfSessions));
          }
        });
      } else if (initialTicketTypes && initialTicketTypes.length > 0) {
        initialTicketTypes.forEach((tt: any) => {
          if (tt.price !== undefined) {
            prices.push(tt.price);
          }
        });
      } else if (ticketTypesV2.length > 0) {
        ticketTypesV2.forEach((tt: any) => {
          prices.push(deriveV2SessionPrice(tt, numberOfSessions));
        });
      }

      if (prices.length > 0) {
        return Math.min(...prices);
      }

      // Deprecated denormalised session.price: last resort only — it goes
      // stale when tickets are repriced, so it must never shadow a live
      // ticket-derived price above.
      if (s.price !== undefined) {
        return s.price;
      }

      return course?.price;
    },
    [
      course?.price,
      course?.isPriceFrom,
      course?.priceDisplayMode,
      initialTicketTypes,
      ticketTypesV2,
    ],
  );

  // Map sessions
  const mapSession = useCallback(
    (s: any): Session => ({
      id: s._id,
      date: s.startDateTime,
      title: s.title,
      description: s.description,
      image: s.image,
      startDateTime: s.startDateTime,
      duration: s.duration,
      availability: s.availability,
      isOnline: s.isOnline ?? course?.isOnline,
      isTerm: false,
      location: getSessionLocation(s),
      price: getSessionPrice(s),
      tutorId: s.tutor,
      tutorName: s.tutorName || s.tutor?.name,
      tutorImage: s.tutorImage || s.tutor?.image?.url,
    }),
    [getSessionLocation, getSessionPrice, course?.isOnline],
  );

  // Sessions state
  const [sessions, setSessions] = useState<Session[]>(() =>
    (initialSessions ?? []).map(mapSession),
  );
  const [selectedSession, setSelectedSession] = useState<Session | null>(() =>
    session ? mapSession(session) : null,
  );
  const [selectedSessions, setSelectedSessions] = useState<Session[]>([]);
  const [isFullyLoaded, setIsFullyLoaded] = useState(
    initialSessions !== undefined && initialSessions.length < 15,
  );
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [hideSoldOut, setHideSoldOut] = useState(false);
  const [locationFilter, setLocationFilterState] = useState<string | null>(
    null,
  );
  const loadRequestIdRef = useRef(0);
  const hasInitialLoadRef = useRef(false);

  const hasAppliedInitialSessionRef = useRef(false);

  // Ticket selections
  const [ticketSelections, setTicketSelections] = useState<TicketSelection[]>(
    [],
  );

  // --- V2 pricing (delegated to useV2Pricing) ---
  const rawSession = selectedSession
    ? rawSessionsRef.current.find((rs) => rs._id === selectedSession.id)
    : null;

  const v2 = useV2Pricing({
    enabled: isV2,
    ticketTypesV2,
    ticketSelections,
    selectedSession,
    rawSessionTicketTypes: rawSession?.ticketTypes ?? [],
    numberOfSessions: 1,
    courseTimezone: course?.timezone ?? "Europe/London",
    discount: base.discount,
    addOnsTotal: base.addOnsTotal,
    bundleID,
    selectedSessions: isMultiDateEnabled ? selectedSessions.map((s) => ({ date: s.startDateTime })) : undefined,
    priorPurchasedTicketIds,
    customerBookingHistory,
  });

  // --- V1 ticket types (only used when NOT V2) ---
  const v1TicketTypes = useMemo(
    (): TicketType[] => {
      if (isV2) return [];

      const rawSess = selectedSession
        ? rawSessionsRef.current.find((rs) => rs._id === selectedSession.id)
        : null;

      let ticketTypesToMap = initialTicketTypes ?? [];
      if (rawSess?.ticketTypes && rawSess.ticketTypes.length > 0) {
        ticketTypesToMap = (initialTicketTypes ?? []).filter((tt: any) => {
          if (!tt || !tt._id) return false;
          const sessionTicketType = rawSess.ticketTypes.find(
            (stt: any) => stt.ticketTypeId === tt._id,
          );
          return sessionTicketType && sessionTicketType.status === "enabled";
        });
      }

      return ticketTypesToMap.map((tt: any) => {
        let basePrice = tt.price;

        if (tt.pricesAvailable?.length && selectedSession?.startDateTime) {
          const sortedPrices = [...tt.pricesAvailable].sort(
            (a: any, b: any) =>
              new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime(),
          );
          const applicablePrice = sortedPrices.find(
            (p: any) =>
              new Date(p.fromDate) < new Date(selectedSession.startDateTime),
          );
          if (applicablePrice) {
            basePrice = applicablePrice.price;
          }
        }

        const effectivePrice = basePrice;

        const sessionTicketType = rawSess?.ticketTypes?.find(
          (stt: any) => stt.ticketTypeId === tt._id,
        );

        return {
          id: tt._id,
          name: tt.name,
          description: tt.description,
          price: tt.price,
          effectivePrice,
          maxQuantity: tt.maxQuantity ?? Infinity,
          minQuantity: tt.minQuantity ?? 0,
          soldOut: tt.soldOut,
          numberOfGuestsPerTicket: tt.numberOfGuestsPerTicket ?? 1,
          // Soft age range (booking seats Phase 5): warns, never blocks.
          minAge: tt.minAge ?? null,
          maxAge: tt.maxAge ?? null,
          maxLimit: sessionTicketType?.maxLimit ?? tt.maxLimit,
          currentSold: sessionTicketType?.currentSold ?? tt.currentSold ?? 0,
          isInTimeConstraint: tt.isInTimeConstraint,
          typeCharge: tt.typeCharge,
          ticketTypeGroup: tt.ticketTypeGroup,
          depositAmount: tt.depositAmount,
          pricesAvailable: tt.pricesAvailable,
          bookingPolicyId: tt.bookingPolicyId ?? null,
          bookingPolicy: tt.bookingPolicy ?? null,
        };
      });
    },
    [initialTicketTypes, selectedSession, isV2],
  );

  // Unified ticket types: V2 hook or V1 computation
  const ticketTypes = isV2 ? v2.ticketTypes : v1TicketTypes;

  // V1 helper to get effective price based on session date and deposit status
  const getTicketPrice = useCallback(
    (ticketType: TicketType, useDeposit: boolean = false): number => {
      let effectivePrice = ticketType.price;

      if (
        ticketType.pricesAvailable?.length &&
        selectedSession?.startDateTime
      ) {
        const sortedPrices = [...ticketType.pricesAvailable].sort(
          (a, b) =>
            new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime(),
        );
        const applicablePrice = sortedPrices.find(
          (p) => new Date(p.fromDate) < new Date(selectedSession.startDateTime),
        );
        if (applicablePrice) {
          effectivePrice = applicablePrice.price;
        }
      }

      if (
        useDeposit &&
        ticketType.ticketTypeGroup === "deposit" &&
        ticketType.depositAmount
      ) {
        effectivePrice = ticketType.depositAmount;
      }

      return effectivePrice;
    },
    [selectedSession],
  );

  // Pre-fill personal details from bundle if using purchased bundle
  useEffect(() => {
    if (bundle) {
      const nameParts = bundle.buyerName?.split(" ") || [];
      const bundleGuest: Guest = {
        ...defaultGuest,
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: bundle.buyerEmail || "",
        phoneNumber: bundle.buyerPhone || "",
      };
      base.setPersonalDetails((prev) => ({
        ...prev,
        guests: [bundleGuest],
      }));
    }
  }, [bundle]);

  // Current step — start at tickets if a session (object) or session ID is
  // pre-selected. The session itself may not be loaded yet; we land on the
  // tickets step optimistically and the flow renders a loading state until
  // the matching session arrives via loadMoreDates().
  const [step, setStep] = useState<CheckoutStep>(() =>
    session || initialSessionId || isEventListing ? "tickets" : "dates",
  );

  // Event listings have exactly one session — select it as soon as it loads so
  // the tickets step has its availability and per-session ticket config.
  useEffect(() => {
    if (!isEventListing || selectedSession || sessions.length === 0) return;
    const only = sessions[0]!;
    setSelectedSession(only);
    if (isMultiDateEnabled) {
      setSelectedSessions([only]);
    }
    setStep("tickets");
  }, [isEventListing, selectedSession, sessions, isMultiDateEnabled]);

  // An event whose date never arrives (stale deep link, date already passed)
  // has nothing to sell. Without a date step in the way, the tickets step would
  // spin forever — fall back to the date step's empty/waitlist state instead.
  const eventSessionMissing =
    isEventListing && !selectedSession && sessions.length === 0 && isFullyLoaded;

  useEffect(() => {
    if (eventSessionMissing && step !== "dates") {
      setStep("dates");
    }
  }, [eventSessionMissing, step]);

  // Pre-select session by ID when initialSessionId is provided and sessions are loaded
  useEffect(() => {
    if (
      !initialSessionId ||
      hasAppliedInitialSessionRef.current ||
      sessions.length === 0
    ) {
      return;
    }

    const matchingSession = sessions.find((s) => s.id === initialSessionId);

    if (matchingSession) {
      hasAppliedInitialSessionRef.current = true;
      setSelectedSession(matchingSession);
      // A SOLD-OUT preselected session (e.g. a queue-held single session / drop-in
      // reached via a timetable "Book Now" deep link) must land on the dates
      // step's sold-out gate, not auto-advance into a doomed checkout. Uses
      // EFFECTIVE availability (server availability minus the buyer's own
      // basket-held seats, matching the date card); a claimer's ?offer= token
      // keeps their held seats visible server-side, so they still auto-advance.
      if (
        !isEventListing &&
        effectivePreselectAvailability(matchingSession, reservedSessionQuantities) <= 0
      ) {
        setStep("dates");
        return;
      }
      if (isMultiDateEnabled) {
        setSelectedSessions([matchingSession]);
      }
      setStep("tickets");
    }
  }, [initialSessionId, sessions, reservedSessionQuantities]);

  // Fallback: if the initial load completed but the target session was never
  // matched (course/session linkage off, expired, etc.), drop back to the
  // date step so the user isn't stuck on an empty tickets view.
  useEffect(() => {
    if (
      !initialSessionId ||
      hasAppliedInitialSessionRef.current ||
      base.isLoading ||
      selectedSession
    ) {
      return;
    }
    if (sessions.length > 0 || isFullyLoaded) {
      hasAppliedInitialSessionRef.current = true;
      // An event has no dates step to fall back to — the sole-session effect
      // above owns selection, so leave the flow where it is.
      if (!isEventListing) {
        setStep("dates");
      }
    }
  }, [initialSessionId, base.isLoading, sessions, isFullyLoaded, selectedSession, isEventListing]);

  // --- V1 total calculation ---
  const v1Total = useMemo(() => {
    if (isV2 || bundleID) return 0;

    const ticketsTotal = ticketSelections.reduce((sum, selection) => {
      const ticketType = v1TicketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      if (!ticketType) return sum;
      const isDeposit = ticketType.ticketTypeGroup === "deposit";
      const effectivePrice = getTicketPrice(ticketType, isDeposit);
      return sum + effectivePrice * selection.quantity;
    }, 0);

    const grossTotal = ticketsTotal + base.addOnsTotal;

    let disc = 0;
    if (base.discount) {
      const { value, type } = base.discount;
      // A code that excludes add-ons (DEV-327) is priced on the tickets only.
      const discBase = codeBasePence(base.discount, grossTotal, base.addOnsTotal);
      if (type === "PERCENTAGE") {
        disc = Math.round((discBase * Math.min(value, 100)) / 100);
      } else {
        disc = Math.min(discBase, value);
      }
    }

    return Math.max(0, grossTotal - disc);
  }, [
    isV2,
    ticketSelections,
    v1TicketTypes,
    base.addOnsTotal,
    base.discount,
    getTicketPrice,
    bundleID,
  ]);

  // Unified total
  const total = bundleID ? 0 : isV2 ? v2.total : v1Total;

  // V1 remaining balance for deposit tickets
  const v1RemainingBalance = useMemo(() => {
    if (isV2) return 0;
    return ticketSelections.reduce((sum, selection) => {
      const ticketType = v1TicketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      if (!ticketType) return sum;
      if (ticketType.ticketTypeGroup !== "deposit") return sum;

      const fullPrice = getTicketPrice(ticketType, false);
      const depositPrice = getTicketPrice(ticketType, true);
      return sum + (fullPrice - depositPrice) * selection.quantity;
    }, 0);
  }, [isV2, ticketSelections, v1TicketTypes, getTicketPrice]);

  const totalRemainingBalance = isV2
    ? v2.totalRemainingBalance
    : v1RemainingBalance;

  // V1 discount amount for display
  const v1DiscountAmount = useMemo(() => {
    if (isV2 || bundleID || !base.discount) return 0;

    const ticketsTotal = ticketSelections.reduce((sum, selection) => {
      const ticketType = v1TicketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      if (!ticketType) return sum;
      const isDeposit = ticketType.ticketTypeGroup === "deposit";
      const effectivePrice = getTicketPrice(ticketType, isDeposit);
      return sum + effectivePrice * selection.quantity;
    }, 0);

    const grossTotal = ticketsTotal + base.addOnsTotal;
    const { value, type } = base.discount;
    // A code that excludes add-ons (DEV-327) is priced on the tickets only.
    const discBase = codeBasePence(base.discount, grossTotal, base.addOnsTotal);

    if (type === "PERCENTAGE") {
      return Math.round((discBase * Math.min(value, 100)) / 100);
    }
    return Math.min(discBase, value);
  }, [
    isV2,
    ticketSelections,
    v1TicketTypes,
    base.addOnsTotal,
    base.discount,
    getTicketPrice,
    bundleID,
  ]);

  const discountAmount = isV2 ? v2.discountAmount : v1DiscountAmount;

  // Unified constraint errors
  const constraintErrors = v2.constraintErrors;

  // Check for mixed booking policies across selected tickets
  const hasMixedPolicies = useMemo(() => {
    const selectedTicketTypeIds = ticketSelections
      .filter((s) => s.quantity > 0)
      .map((s) => s.ticketTypeId);
    if (selectedTicketTypeIds.length <= 1) return false;

    const policyIds = selectedTicketTypeIds
      .map((id) => ticketTypes.find((tt) => tt.id === id)?.bookingPolicyId)
      .filter((id): id is string => id != null);

    if (policyIds.length <= 1) return false;
    return new Set(policyIds).size > 1;
  }, [ticketSelections, ticketTypes]);

  // Is free
  const isFree = useMemo(() => {
    if (bundleID) return true;
    return ticketSelections.some((s) => s.quantity > 0) && total === 0;
  }, [ticketSelections, total, bundleID]);

  // Remaining availability
  const remainingAvailability = useMemo(() => {
    if (!selectedSession) return 0;

    const baseAvailability = selectedSession.availability ?? 0;

    const selectedSeats = ticketSelections.reduce((sum, selection) => {
      const ticketType = ticketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      const guestsPerTicket = ticketType?.numberOfGuestsPerTicket ?? 1;
      return sum + selection.quantity * guestsPerTicket;
    }, 0);

    return Math.max(0, baseAvailability - selectedSeats);
  }, [selectedSession, ticketSelections, ticketTypes]);

  // Multi-date: compute minimum availability across all selected sessions
  const multiDateAvailability = useMemo(() => {
    if (!isMultiDateEnabled || selectedSessions.length === 0) return null;

    let minAvailability = Infinity;
    let restrictiveSessions: Session[] = [];

    for (const session of selectedSessions) {
      if (session.availability < minAvailability) {
        minAvailability = session.availability;
        restrictiveSessions = [session];
      } else if (session.availability === minAvailability) {
        restrictiveSessions.push(session);
      }
    }

    const hasRestriction = selectedSessions.some(
      (s) => s.availability !== minAvailability,
    );

    return {
      minAvailability: minAvailability === Infinity ? 0 : minAvailability,
      restrictiveSessions,
      hasRestriction,
    };
  }, [isMultiDateEnabled, selectedSessions]);

  // Multi-date: `total` from useV2Pricing already aggregates across every
  // selected session (evaluating per-session day-of-week / time rules). So
  // `multiDateTotal` is just `total` — no `× dateCount` (that double-counted
  // after the per-session pricing refactor). The "N × £perDate" summary
  // breakdown computes per-date = aggregate / dateCount at the call site.
  const dateCount = isMultiDateEnabled ? selectedSessions.length : 1;
  const multiDateTotal = total;

  // Total guest count (seats) derived from current ticket selections.
  // Seats grouped by ticket, the one derivation every seat count reads from
  // (mirrors the server's computeReserveSeatCounts / BookingSeat.planSeats).
  const ticketSeatGroups = useMemo(
    () => buildTicketSeatGroups(ticketSelections, ticketTypes),
    [ticketSelections, ticketTypes],
  );
  const totalGuestCount = useMemo(() => totalSeatCount(ticketSeatGroups), [ticketSeatGroups]);

  // How this listing collects guests: additional guests come via the booking
  // form when it carries the (enabled) system identity questions; without
  // them, guest details simply aren't collected. The buyer is always
  // collected at the basket page, so there is no in-flow personal step.
  const { collectSystemGuests } = resolveGuestCollectionMode(
    course.userQuestionForm?.questions,
    {
      formEnabled: course.userQuestionForm?.isEnabled,
    },
  );

  // Visible steps
  const visibleSteps = useMemo(() => {
    const steps: CheckoutStep[] =
      isEventListing && !eventSessionMissing
        ? ["tickets"]
        : ["dates", "tickets"];

    if (enabledAddOns.length > 0 || addOnGroups.length > 0) {
      steps.push("addons");
    }

    // Participants ("Who is attending?") — collected before the booking-question
    // form. When enabled it replaces the system guest-identity questions.
    if (showParticipantsStep) {
      steps.push("participants");
    }

    // Show the booking-question step when the form is enabled and at least one
    // question actually renders for this booking. A form containing only system
    // identity questions is skipped when there are no additional guests (or when
    // participants own identity collection).
    if (
      course.userQuestionForm?.isEnabled &&
      hasRenderableQuestions(
        course.userQuestionForm?.questions,
        totalGuestCount,
        collectSystemGuests,
        showParticipantsStep,
      )
    ) {
      steps.push("userform");
    }

    steps.push("basketChoice");

    return steps;
  }, [
    enabledAddOns,
    addOnGroups,
    course.userQuestionForm,
    collectSystemGuests,
    totalGuestCount,
    showParticipantsStep,
    isEventListing,
    eventSessionMissing,
  ]);

  // Step navigation
  const currentStepIndex = visibleSteps.indexOf(step);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;

  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < visibleSteps.length) {
      const nextStep = visibleSteps[nextIndex]!;
      setStep(nextStep);
      onStepChange?.();
    }
  }, [currentStepIndex, visibleSteps, onStepChange]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = visibleSteps[prevIndex]!;
      setStep(prevStep);
      onStepChange?.();
    }
  }, [currentStepIndex, visibleSteps, onStepChange]);

  const goToStep = useCallback(
    (targetStep: CheckoutStep) => {
      if (visibleSteps.includes(targetStep)) {
        setStep(targetStep);
        onStepChange?.();
      }
    },
    [visibleSteps, onStepChange],
  );

  // Session selection.
  //
  // Both of these read `sessions`/`selectedSessions` straight off the render
  // closure and call the setters with plain values. They must NOT look the
  // session up from inside a `setSessions(prev => ...)` updater: a state
  // updater has to be pure, and React re-invokes it (twice on every update
  // under StrictMode, and again whenever a render is rebased or interrupted).
  // An updater that dispatches another setState as a side effect therefore
  // fires that dispatch more than once — which made `toggleSession` select a
  // date and immediately deselect it, so no date could ever be picked.
  const selectSession = useCallback(
    (sessionId: string) => {
      const session = sessions.find((s) => s.id === sessionId);
      if (session) {
        setSelectedSession(session);
      }
    },
    [sessions],
  );

  // Multi-date: toggle session selection
  const toggleSession = useCallback(
    (sessionId: string) => {
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) return;

      const isAlreadySelected = selectedSessions.some((s) => s.id === sessionId);
      let newSelected: Session[];
      if (isAlreadySelected) {
        newSelected = selectedSessions.filter((s) => s.id !== sessionId);
      } else {
        if (selectedSessions.length >= maxDateSelections) return;
        newSelected = [...selectedSessions, session];
      }

      setSelectedSessions(newSelected);
      setSelectedSession(newSelected.length > 0 ? newSelected[0]! : null);
    },
    [sessions, selectedSessions, maxDateSelections],
  );

  // Ticket selection.
  //
  // Same rule as the session callbacks above: the next selections and the guest
  // count are derived from the render closure, then both setters fire as plain,
  // batched dispatches. Calling `setPersonalDetails` from inside the
  // `setTicketSelections` updater dispatched it twice per update under
  // StrictMode; that was survivable only because padding/trimming `guests` to a
  // target length happens to be idempotent, which is luck, not a contract.
  const updateTicketSelection = useCallback(
    (ticketTypeId: string, quantity: number) => {
      const existingIndex = ticketSelections.findIndex(
        (sel) => sel.ticketTypeId === ticketTypeId,
      );

      let newSelections: TicketSelection[];
      if (quantity === 0) {
        newSelections = ticketSelections.filter(
          (sel) => sel.ticketTypeId !== ticketTypeId,
        );
      } else if (existingIndex >= 0) {
        newSelections = [...ticketSelections];
        newSelections[existingIndex] = { ticketTypeId, quantity };
      } else {
        newSelections = [...ticketSelections, { ticketTypeId, quantity }];
      }

      const totalGuests = newSelections.reduce((sum, sel) => {
        const ticketType = ticketTypesRef.current.find(
          (tt: any) => tt._id === sel.ticketTypeId,
        );
        const guestsPerTicket = ticketType?.numberOfGuestsPerTicket ?? 1;
        return sum + (sel.quantity * guestsPerTicket);
      }, 0);

      setTicketSelections(newSelections);
      // Still an updater, deliberately: it has to read the guests the personal
      // details step may have edited. It is pure — it only pads/trims `prev`
      // to `totalGuests` — so a replayed render re-running it is a no-op.
      base.setPersonalDetails((prev) => {
        const newGuests = [...prev.guests];
        while (newGuests.length < totalGuests) {
          newGuests.push({ ...defaultGuest, guest: true });
        }
        while (newGuests.length > totalGuests && newGuests.length > 1) {
          newGuests.pop();
        }
        return { ...prev, guests: newGuests };
      });
    },
    [ticketSelections],
  );

  // Load more dates
  const loadMoreDates = useCallback(async () => {
    if (base.isLoading || isFullyLoaded) return;

    const requestId = ++loadRequestIdRef.current;
    base.setIsLoading(true);
    try {
      const isFirstLoad = page.current === 0;
      const data = await onLoadMoreDates({
        page: ++page.current,
        limit: 15,
        sessionId: isFirstLoad ? initialSessionId : undefined,
        fromDate: fromDate ? fromDate.toISOString() : undefined,
        locationId: locationFilter ?? undefined,
      });

      // Drop responses from stale filter states
      if (requestId !== loadRequestIdRef.current) return;

      const rawNewSessions = Array.isArray(data) ? data : (data.sessions ?? []);
      rawSessionsRef.current = [...rawSessionsRef.current, ...rawNewSessions];
      const newSessions = rawNewSessions.map(mapSession);

      setSessions((prev) => [...prev, ...newSessions]);
      setIsFullyLoaded(newSessions.length < 15);
    } catch (error) {
      if (requestId === loadRequestIdRef.current) {
        setIsFullyLoaded(true);
      }
    } finally {
      if (requestId === loadRequestIdRef.current) {
        base.setIsLoading(false);
      }
    }
  }, [
    base.isLoading,
    isFullyLoaded,
    onLoadMoreDates,
    mapSession,
    initialSessionId,
    fromDate,
    locationFilter,
  ]);

  // Set (or clear) the "from date" filter — resets the list and refetches
  const setDateFilter = useCallback(
    async (date: Date | null) => {
      const requestId = ++loadRequestIdRef.current;

      setFromDate(date);
      page.current = 0;
      rawSessionsRef.current = [];
      setSessions([]);
      setIsFullyLoaded(false);
      base.setIsLoading(true);

      try {
        const data = await onLoadMoreDates({
          page: ++page.current,
          limit: 15,
          fromDate: date ? date.toISOString() : undefined,
          locationId: locationFilter ?? undefined,
        });

        if (requestId !== loadRequestIdRef.current) return;

        const rawNewSessions = Array.isArray(data) ? data : (data.sessions ?? []);
        rawSessionsRef.current = rawNewSessions;
        const newSessions = rawNewSessions.map(mapSession);
        setSessions(newSessions);
        setIsFullyLoaded(newSessions.length < 15);
      } catch (error) {
        if (requestId === loadRequestIdRef.current) {
          setIsFullyLoaded(true);
        }
      } finally {
        if (requestId === loadRequestIdRef.current) {
          base.setIsLoading(false);
        }
      }
    },
    [onLoadMoreDates, mapSession, base, locationFilter],
  );

  // Set (or clear) the location filter — resets the list and refetches.
  // Composes with the active from-date filter; both are server-side params.
  const setLocationFilter = useCallback(
    async (locationId: string | null) => {
      const requestId = ++loadRequestIdRef.current;

      setLocationFilterState(locationId);
      page.current = 0;
      rawSessionsRef.current = [];
      setSessions([]);
      setIsFullyLoaded(false);
      base.setIsLoading(true);

      try {
        const data = await onLoadMoreDates({
          page: ++page.current,
          limit: 15,
          fromDate: fromDate ? fromDate.toISOString() : undefined,
          locationId: locationId ?? undefined,
        });

        if (requestId !== loadRequestIdRef.current) return;

        const rawNewSessions = Array.isArray(data) ? data : (data.sessions ?? []);
        rawSessionsRef.current = rawNewSessions;
        const newSessions = rawNewSessions.map(mapSession);
        setSessions(newSessions);
        setIsFullyLoaded(newSessions.length < 15);
      } catch (error) {
        if (requestId === loadRequestIdRef.current) {
          setIsFullyLoaded(true);
        }
      } finally {
        if (requestId === loadRequestIdRef.current) {
          base.setIsLoading(false);
        }
      }
    },
    [onLoadMoreDates, mapSession, base, fromDate],
  );

  // Initial load — guarded so it runs exactly once. Without the ref, React
  // StrictMode's dev double-invoke (and any other re-fire of this mount effect)
  // calls loadMoreDates() twice; both advance page.current, and the
  // stale-request guard then discards the page-1 response, leaving only an
  // often-empty page 2 ("No dates available at the moment.").
  useEffect(() => {
    if (initialSessions === undefined && !hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      loadMoreDates();
    }
  }, []);

  // Apply discount
  const applyDiscount = useCallback(
    async (code: string) => {
      if (!onValidateDiscount) return;

      const email = base.personalDetails.guests[0]?.email || "";
      base.setIsLoading(true);
      base.clearError();

      try {
        const result = await onValidateDiscount(code, email);
        if (result && result.value !== undefined) {
          base.setDiscount({
            value: result.value,
            type: result.type,
            percentage: result.percentage,
            excludeAddOns: result.excludeAddOns,
          });
          base.setDiscountCode(result.code || code);
        } else {
          const errorMsg = result?.message || "Invalid discount code";
          base.setError(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        const errorMsg =
          error?.response?.data?.message ||
          (error instanceof Error ? error.message : null) ||
          "Failed to validate discount";
        base.setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        base.setIsLoading(false);
      }
    },
    [onValidateDiscount, base],
  );

  // Clear discount
  const clearDiscount = useCallback(() => {
    base.setDiscount(null);
    base.setDiscountCode("");
  }, [base]);

  // Validate current step
  const canContinue = useCallback((): boolean => {
    let result = false;
    switch (step) {
      case "dates":
        result = isMultiDateEnabled
          ? selectedSessions.length > 0
          : selectedSession !== null;
        break;
      case "tickets": {
        const hasTickets = ticketSelections.some((s) => s.quantity > 0);
        if (!hasTickets) {
          result = false;
          break;
        }
        result = constraintErrors.length === 0 && !hasMixedPolicies;
        break;
      }
      case "addons":
        result = base.addOnGroupsValid;
        break;
      case "participants":
        result = base.isParticipantsValid;
        break;
      case "userform":
        result = base.isUserFormValid;
        break;
      case "basketChoice":
        result = true;
        break;
      default:
        result = false;
    }
    return result;
  }, [step, selectedSession, selectedSessions, ticketSelections, base, isMultiDateEnabled, constraintErrors, hasMixedPolicies]);

  // Get booking data for API
  const getBookingData = useCallback(() => {
    const { personalDetails, userFormValues, addOnSelections } = base;
    const [guest, ...additionalGuests] = personalDetails.guests;

    const tickets = ticketSelections
      .map((sel) => {
        const ticketType = (initialTicketTypes ?? []).find(
          (tt: any) => tt._id === sel.ticketTypeId,
        );
        return ticketType ? { ...ticketType, count: sel.quantity } : null;
      })
      .filter(Boolean);

    const allAddOnsLookup = [
      ...enabledAddOns,
      ...addOnGroups.flatMap((g: any) => g.addOns),
    ];
    const selectedAddOns = addOnSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const addOn = allAddOnsLookup.find(
          (a: any) => a.id === sel.addOnId,
        );
        return {
          _id: addOn?.id,
          id: addOn?.id,
          name: addOn?.name,
          price: addOn?.price,
          quantity: sel.quantity,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    const data: any = {
      bookingType: "regular",
      isRegular: true,
      tickets,
      user: {
        _id: user?._id || user?.id,
        profile: {
          firstName: guest?.firstName,
          lastName: guest?.lastName,
          phone: guest?.phoneNumber,
        },
        renderEmail: guest?.email,
      },
      checkoutGuestsInformation: personalDetails.guests,
      marketingOptIn: guest?.marketingOptIn,
      courseId: course._id,
      startDateTime: selectedSession?.startDateTime,
      additionalGuests,
      bundleID: bundleID,
      discountCode: base.discountCode,
      credit: 0,
      sessionid: selectedSession?.id,
      deliveryAddress: isSendingMaterial
        ? personalDetails.deliveryAddress
        : null,
      userInputs: userFormValues,
      participants: showParticipantsStep
        ? participantsToPayload(base.participantsValue)
        : undefined,
      addOns: selectedAddOns,
      timezone: course.timezone,
      _pricingSummary: {
        grossTotal: total + discountAmount,
        netTotal: total,
        discountAmount,
        discountType: bundleID ? "bundle" : base.discount ? "discount" : undefined,
        currency: course.currency || "GBP",
      },
    };

    if (isV2) {
      Object.assign(data, v2.buildBookingData());
    }

    return data;
  }, [
    base,
    ticketSelections,
    initialTicketTypes,
    enabledAddOns,
    addOnGroups,
    selectedSession,
    course,
    user,
    bundleID,
    isSendingMaterial,
    total,
    discountAmount,
    isV2,
    v2.buildBookingData,
  ]);

  return {
    // State
    step,
    sessions,
    selectedSession,
    selectedSessions,
    ticketTypes,
    ticketSelections,
    addOnSelections: base.addOnSelections,
    userFormValues: base.userFormValues,
    personalDetails: base.personalDetails,
    discount: base.discount,
    discountCode: base.discountCode,
    error: base.error,
    isLoading: base.isLoading,
    isFullyLoaded,
    fromDate,
    locationFilter,
    hideSoldOut,
    isConfirmingFreeBooking: base.isConfirmingFreeBooking,
    isUserFormValid: base.isUserFormValid,
    participantsValue: base.participantsValue,
    isParticipantsValid: base.isParticipantsValid,
    buyerAttendeeSeededRef: base.buyerAttendeeSeededRef,
    showParticipantsStep,
    isMultiDateEnabled,
    maxDateSelections,
    constraintErrors,
    hasMixedPolicies,
    v2PricingResults: v2.pricingResults,
    v2InstalmentDiscountShares: v2.instalmentDiscountShares,
    isV2,

    // Computed
    total,
    totalRemainingBalance,
    discountAmount,
    isFree,
    remainingAvailability,
    multiDateAvailability,
    dateCount,
    multiDateTotal,
    visibleSteps,
    totalGuestCount,
    ticketSeatGroups,
    collectSystemGuests,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    enabledAddOns,
    addOnGroups,
    addOnGroupsValid: base.addOnGroupsValid,
    addOnsTotal: base.addOnsTotal,
    course,
    bundleID,
    availability: selectedSession?.availability ?? 0,

    // Actions
    goToNextStep,
    goToPreviousStep,
    goToStep,
    selectSession,
    toggleSession,
    updateTicketSelection,
    updateAddOnSelection: base.updateAddOnSelection,
    updateUserFormValue: base.updateUserFormValue,
    setUserFormValues: base.setUserFormValues,
    setIsUserFormValid: base.setIsUserFormValid,
    setParticipantsValue: base.setParticipantsValue,
    setIsParticipantsValid: base.setIsParticipantsValid,
    updatePersonalDetails: base.updatePersonalDetails,
    updateDeliveryAddress: base.updateDeliveryAddress,
    loadMoreDates,
    setDateFilter,
    setLocationFilter,
    setHideSoldOut,
    applyDiscount,
    clearDiscount,
    canContinue,
    getBookingData,
    setIsConfirmingFreeBooking: base.setIsConfirmingFreeBooking,
    clearError: base.clearError,
  };
}
